import { create } from "zustand";
import { persist } from "zustand/middleware";
import { apiFetch } from "@/lib/jwt-auth-client";

export interface OrganizationInfo {
  id: string;
  name: string;
  slug: string;
  role: string;
  created_at: string;
}

interface OrganizationState {
  organizations: OrganizationInfo[];
  activeOrganizationId: string | null;
  loaded: boolean;

  setOrganizations: (organizations: OrganizationInfo[]) => void;
  setActiveOrganizationId: (id: string) => void;
  getActiveOrganization: () => OrganizationInfo | null;

  fetchOrganizations: () => Promise<OrganizationInfo[]>;
  createOrganization: (name: string) => Promise<OrganizationInfo>;
  switchOrganization: (id: string) => void;
  reset: () => void;
}

export const useOrganizationStore = create<OrganizationState>()(
  persist(
    (set, get) => ({
      organizations: [],
      activeOrganizationId: null,
      loaded: false,

      setOrganizations: (organizations) => set({ organizations, loaded: true }),
      setActiveOrganizationId: (id) => set({ activeOrganizationId: id }),

      getActiveOrganization: () => {
        const { organizations, activeOrganizationId } = get();
        return organizations.find((w) => w.id === activeOrganizationId) ?? null;
      },

      fetchOrganizations: async () => {
        const res = await apiFetch("/auth/organizations/");
        if (!res.ok) return [];
        const data = (await res.json()) as OrganizationInfo[];
        const { activeOrganizationId } = get();

        set({ organizations: data, loaded: true });

        // Auto-select first organization if none selected or current is invalid
        if (
          data.length > 0 &&
          (!activeOrganizationId || !data.find((w) => w.id === activeOrganizationId))
        ) {
          set({ activeOrganizationId: data[0].id });
        }

        return data;
      },

      createOrganization: async (name: string) => {
        const res = await apiFetch("/auth/organizations/", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name }),
        });
        if (!res.ok) throw new Error("Failed to create organization");
        const organization = (await res.json()) as OrganizationInfo;
        const { organizations } = get();
        set({
          organizations: [...organizations, organization],
          activeOrganizationId: organization.id,
        });
        return organization;
      },

      switchOrganization: (id: string) => {
        set({ activeOrganizationId: id });
      },

      reset: () => set({ organizations: [], activeOrganizationId: null, loaded: false }),
    }),
    {
      name: "wiser-organization",
      partialize: (state) => ({
        activeOrganizationId: state.activeOrganizationId,
      }),
    },
  ),
);
